import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { categoryName, formatHeat, formatLike } from '@/utils/content';

interface ArticleCardProps {
  article: Article;
  variant?: 'grid' | 'row';
  showCategory?: boolean;
}

export default function ArticleCard({ article, variant = 'grid', showCategory = true }: ArticleCardProps) {
  const cat = categoryName(article.categoryId);

  if (variant === 'row') {
    return (
      <article className="group flex gap-4 rounded-xl border border-cream-200 bg-white p-3 transition-all hover:-translate-y-0.5 hover:border-primary-200">
        <Link to={`/article/${article.id}`} className="block shrink-0 overflow-hidden rounded-lg">
          <img
            src={article.cover}
            alt={article.coverAlt}
            loading="lazy"
            className="h-20 w-24 object-cover object-top transition-transform duration-300 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-col justify-center">
          <Link
            to={`/article/${article.id}`}
            className="line-clamp-2 text-sm font-bold leading-snug text-ink transition-colors hover:text-primary-600"
          >
            {article.title}
          </Link>
          <div className="mt-2 flex items-center gap-3 text-xs text-ink-600">
            {showCategory && (
              <span className="rounded-full bg-cream-100 px-2 py-0.5 font-medium text-ink">{cat}</span>
            )}
            <span>
              <i className="ri-fire-line mr-0.5 text-primary-500" />
              {formatHeat(article.heat)}
            </span>
            <span>
              <i className="ri-eye-line mr-0.5" />
              {formatLike(article.commentCount)}评
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-cream-200 bg-white transition-all hover:-translate-y-1 hover:border-primary-200">
      <Link to={`/article/${article.id}`} className="relative block overflow-hidden">
        <img
          src={article.cover}
          alt={article.coverAlt}
          className="aspect-[10/7] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        {showCategory && (
          <span className="absolute left-3 top-3 rounded-full bg-cream-50/90 px-2.5 py-1 text-xs font-bold text-primary-700">
            {cat}
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <Link
          to={`/article/${article.id}`}
          className="line-clamp-2 text-base font-bold leading-snug text-ink transition-colors hover:text-primary-600"
        >
          {article.title}
        </Link>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-ink-600">{article.excerpt}</p>
        <div className="mt-3 flex items-center justify-between border-t border-cream-100 pt-3 text-xs text-ink-600">
          <span className="flex items-center gap-1">
            <span className="inline-block h-5 w-5 rounded-full bg-primary-100 text-center leading-5 text-primary-600">
              <i className="ri-user-smile-line text-[11px]" />
            </span>
            {article.author}
          </span>
          <span className="flex items-center gap-3">
            <span title="热度">
              <i className="ri-fire-fill text-primary-500" />
              {formatHeat(article.heat)}
            </span>
            <span title="评论">
              <i className="ri-chat-3-line" />
              {formatLike(article.commentCount)}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}